import { Seo } from "../components/Seo";
import { CtaBand, Eyebrow, Faqs } from "../components/Blocks";
import { Link, returnToPreviousPage, useNavigate } from "../components/Layout";
import { siteConfig } from "../config/site";
import { imageSize } from "../data/imageSizes";
import { venueFaqs } from "../data/content";

export default function Venue() {
  const navigate = useNavigate();
  const c = siteConfig.contact;
  const hasAddress = c.address !== "VENUE_ADDRESS";

  return <main>
    <Seo path="/venue" />
    <section className="legal guide-page venue-tey-page">
      <nav className="guide-breadcrumb" aria-label="현재 위치">
        <Link href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>어린이집·유치원 단체관람</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">영유아를 위한 공연장</span>
      </nav>

      <Eyebrow>THEATRE FOR EARLY YEARS · TEY</Eyebrow>
      <h1>아이에게 맞춘 공연은<br />공간부터 다릅니다</h1>
      <p><b>TEY(Theatre for Early Years)는 영유아 관객을 위해 처음부터 새롭게 설계하는 공연예술입니다.</b> 성인 공연의 길이를 줄이거나 설명을 쉽게 바꾸는 데서 끝나지 않고, 아이가 보고 듣고 움직이며 반응하는 방식을 공연의 출발점으로 삼습니다.</p>
      <p>마이리틀클래식의 동탄 공연장은 이 생각을 실제 공간으로 옮긴 곳입니다. 낮은 무대와 바닥 좌석, 연주자와 가까운 거리, 벽면을 채우는 미디어아트는 모두 아이가 자신의 방식으로 공연 안에 머물 수 있게 하기 위한 선택입니다.</p>

      <figure className="venue-tey-lead-image">
        <img src="/images/venue-starry-night.jpg" alt="별이 빛나는 밤 미디어아트 앞에서 라이브 클래식을 연주하고 가까이 앉아 관람하는 영유아들" {...imageSize("/images/venue-starry-night.jpg")} decoding="async" />
        <figcaption>마이리틀클래식 동탄 공연장 · 미디어아트와 실제 연주를 아이의 눈높이에서 가까이 만납니다.</figcaption>
      </figure>

      <article className="guide-content venue-tey-content">
        <section>
          <h2>TEY는 성인 공연의 작은 버전이 아닙니다</h2>
          <p>영유아는 성인과 같은 방식으로 공연을 보지 않습니다. 한곳을 오래 바라보기보다 소리가 나는 방향으로 몸을 돌리고, 마음에 드는 리듬이 나오면 움직이며, 궁금한 장면에서는 바로 말로 반응합니다.</p>
          <p>그래서 영유아를 위한 공연은 시간과 장면 전환, 관객과의 거리, 참여 방식을 창작의 처음부터 다르게 설계합니다. 조용히 끝까지 앉아 있어야만 성공한 관람이라고 보지 않는 것도 중요한 차이입니다.</p>
        </section>

        <section>
          <h2>움직이고 말하는 것도 관람입니다</h2>
          <p>아이가 웃거나 질문하고, 음악에 맞춰 몸을 움직이는 것은 공연을 방해하는 행동이 아니라 지금 보고 들은 것에 반응하는 방식입니다. 진행자는 그 반응을 멈추게 하기보다 무대로 연결해 다음 장면에 참여할 수 있도록 돕습니다.</p>
          <p><b>공연에 아이를 맞추는 대신, 아이의 반응을 받아들일 수 있도록 공연의 흐름을 열어 두는 것</b>이 마이리틀클래식이 생각하는 첫 관람의 기준입니다.</p>
        </section>

        <section>
          <h2>좋은 첫 공연은 거리를 먼저 바꿉니다</h2>
          <p>성인 관객을 기준으로 지은 공연장은 무대가 높고 객석이 멀 수 있습니다. 작은 아이에게는 연주자의 얼굴과 악기를 자세히 보기 어렵고, 실제 악기의 울림도 멀게 느껴질 수 있습니다.</p>
          <p>동탄 공연장은 아이가 고개를 높이 들지 않아도 연주자와 눈을 맞출 수 있도록 무대를 낮췄습니다. 무대와 객석의 거리를 가까이 두어 손가락의 움직임과 연주자의 호흡까지 자연스럽게 보이도록 했습니다.</p>
        </section>

        <figure className="venue-tey-inside-image">
          <img src="/images/group-visits/01.webp" alt="공연이 끝난 뒤 연주자와 가까이에서 바이올린을 체험하는 아이들" {...imageSize("/images/group-visits/01.webp")} loading="lazy" decoding="async" />
          <figcaption>공연이 끝난 뒤에는 연주자 곁에서 실제 악기의 크기와 소리, 연주 방법을 가까이 경험합니다.</figcaption>
        </figure>

        <section>
          <h2>바닥 좌석은 집중을 포기한 선택이 아닙니다</h2>
          <p>이곳에는 정해진 의자 좌석이 없습니다. 아이들은 연령과 인원에 맞춰 바닥에 편하게 앉고, 인솔자는 곁에서 함께 관람합니다. 몸의 방향을 바꾸거나 잠시 자세를 고쳐 앉아도 다른 관객을 크게 방해하지 않습니다.</p>
          <p>좌석을 없앤 이유는 자유롭게 돌아다니게 하기 위해서가 아니라, <b>몸을 통제하는 데 쓰는 힘을 공연을 보고 듣는 데 사용할 수 있게 하기 위해서</b>입니다.</p>
        </section>

        <section>
          <h2>이야기·음악·영상이 한 감각으로 이어집니다</h2>
          <p>실제 동화책의 이야기를 들려주는 동안 삽화가 공연장 벽면에 펼쳐지고, 장면과 감정에 어울리는 클래식 음악이 전문 연주자의 라이브 연주로 이어집니다. 음악이 시작되면 곡의 분위기를 담은 미디어아트가 공간을 채웁니다.</p>
          <p>아이에게 줄거리와 음악을 따로 설명하기보다, 눈앞의 장면과 실제 악기의 울림을 함께 경험하도록 만드는 방식입니다.</p>
        </section>

        <section>
          <h2>TEY의 원칙을 동탄의 한 공간에 옮겼습니다</h2>
          <p>마이리틀클래식 동탄 공연장은 어린이집·유치원 단체 예약제로 운영합니다. 한 기관이 단독으로 방문하기도 하고, 두 기관이 일정을 맞춰 함께 관람하기도 합니다. 공연 45분과 악기 체험 15분 내외로, 입장과 착석 시간을 제외한 프로그램은 총 1시간입니다.</p>
          {hasAddress && <p><b>주소</b> — {c.address}<br /><b>차량 하차</b> — 건물 앞에서 하차한 뒤 공연장까지 직접 길안내합니다.</p>}
          <p>관람료와 실제 방문 기록은 <Link className="inline-link" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>어린이집·유치원 단체관람 페이지</Link>에서 확인하실 수 있습니다.</p>
          <div className="venue-tey-actions">
            {c.naverPlace && <a className="button wine" href={c.naverPlace} target="_blank" rel="noreferrer">네이버 지도에서 길찾기 <span aria-hidden="true">↗</span></a>}
            <Link className="button ghost" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>관람료·기록 보기 <span aria-hidden="true">→</span></Link>
          </div>
        </section>
      </article>

      <section className="venue-tey-faq">
        <div>
          <Eyebrow>BEFORE YOUR VISIT</Eyebrow>
          <h2>동탄 공연장 방문 전에</h2>
        </div>
        <Faqs items={venueFaqs} />
      </section>

      <nav className="guide-return" aria-label="단체관람 안내로 돌아가기">
        <div><span>GROUP VISIT</span><strong>어린이집·유치원 단체관람 안내</strong></div>
        <Link className="button ghost" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>단체관람 안내로 돌아가기 <span aria-hidden="true">→</span></Link>
      </nav>
    </section>

    <CtaBand
      title="아이들과 함께 영유아를 위한 공연장을 경험해 보세요"
      body="희망 방문일과 아이들의 연령, 인원을 알려주시면 가능한 일정과 가장 적합한 프로그램을 안내합니다."
      type="visit"
    />
  </main>;
}
